import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "../App";

export default function MobileStoryViewer({ userId, userName, userColor, onClose }) {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    // Fetch story data for this user
    api.get(`/team/${userId}/story`).then(r => {
      setSlides(r.data?.slides || []);
      setLoading(false);
    }).catch(() => {
      // Fallback: generate basic slides from available data
      setSlides([{ title: "STATUS", content: "Loading..." }]);
      setLoading(false);
    });
  }, [userId]);

  const next = useCallback(() => {
    if (current < slides.length - 1) setCurrent(c => c + 1);
    else onClose();
  }, [current, slides.length, onClose]);

  const prev = useCallback(() => {
    if (current > 0) setCurrent(c => c - 1);
  }, [current]);

  const getInitials = (name) => (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  if (!userId) return null;

  return createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: '#000', display: 'flex', flexDirection: 'column',
      animation: 'storyIn .25s ease-out',
    }}>
      {/* Progress bars */}
      <div style={{ display: 'flex', gap: 3, padding: '6px 10px', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
        {slides.map((_, i) => (
          <div key={i} style={{ flex: 1, height: 2.5, background: 'rgba(255,255,255,.2)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', background: '#fff', borderRadius: 2,
              width: i < current ? '100%' : i === current ? '100%' : '0%',
              transition: i === current ? 'width 5s linear' : 'none',
            }} />
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 10px 0', position: 'absolute', top: 10, left: 0, right: 0, zIndex: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: userColor || '#666',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 600, color: '#fff',
        }}>{getInitials(userName)}</div>
        <div>
          <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{userName}</div>
          <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 11 }}>Active now</div>
        </div>
        <div onClick={onClose} style={{ marginLeft: 'auto', color: '#fff', fontSize: 22, cursor: 'pointer', padding: 6 }}>&#x2715;</div>
      </div>

      {/* Tap zones */}
      <div onClick={prev} style={{ position: 'absolute', left: 0, top: 55, bottom: 70, width: '30%', zIndex: 5, cursor: 'pointer' }} />
      <div onClick={next} style={{ position: 'absolute', right: 0, top: 55, bottom: 70, width: '70%', zIndex: 5, cursor: 'pointer' }} />

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '55px 20px 70px', color: '#fff' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>Loading...</div>
        ) : slides[current] ? (
          <>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em', color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', marginBottom: 10 }}>
              {slides[current].title}
            </div>
            <div dangerouslySetInnerHTML={{ __html: slides[current].html || '' }} />
            {slides[current].items && (
              <ul style={{ listStyle: 'none', marginTop: 12 }}>
                {slides[current].items.map((item, i) => (
                  <li key={i} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color || '#22C55E', flexShrink: 0 }} />
                    {item.text}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
      </div>

      {/* Bottom: reactions + nudge */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 14px', display: 'flex', gap: 6, alignItems: 'center', zIndex: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['\uD83D\uDC4D', '\uD83D\uDD25', '\uD83D\uDC40'].map(e => (
            <button key={e} onClick={() => alert(`${e} reaction sent!`)}
              style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,.12)', border: 'none', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {e}
            </button>
          ))}
        </div>
        <input placeholder="Send a nudge..."
          style={{ flex: 1, padding: '8px 14px', borderRadius: 18, border: '1px solid rgba(255,255,255,.15)', background: 'transparent', color: '#fff', fontSize: 12, outline: 'none' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value) { alert('Nudge sent!'); e.target.value = ''; }}} />
      </div>

      <style>{`@keyframes storyIn{from{opacity:0;transform:scale(1.03)}to{opacity:1;transform:scale(1)}}`}</style>
    </div>,
    document.body
  );
}
